"use client";

import { LocationContext } from "@/components/framework/locationswitcher/locationReducer";
import { type ProgramShowDisplayProps } from "@/components/program/ProgramShowDisplayProps";
import ShowPosterFallback from "@/components/program/ShowPosterFallback";
import getPlannedDates from "@/lib/client/getPlannedDates";
import getShowsAndMovies, { type ShowWithMovie } from "@/lib/client/getShowsAndMovies";
import { useQuery } from "@tanstack/react-query";
import React, { use, useCallback } from "react";
import styles from "./ProgramLoader.module.css";

//#region [Props]
type ProgramLoaderProps = {
    children: React.ReactNode;
    hidePlannedDates?: boolean;
    movieId?: string;
    smallPlaceholders?: boolean;
    className?: string;
    fallbackSize?: "small" | "large"
};
//#endregion

//#region [Component]
export default function ProgramLoader({ children, hidePlannedDates = false, movieId, smallPlaceholders = false, className, fallbackSize = "large" }: ProgramLoaderProps) {
    const locationContext = use(LocationContext);

    const showLocationTools = useCallback(() => {
        locationContext.dispatcher({ type: "toggleToolsDisplay", value: true });
        if (window) {
            window.scrollTo({ top: 0, behavior: "smooth" });
        }

    }, [locationContext]);

    const filterDataForSelectedLocations = useCallback((data: ShowWithMovie[] | undefined) => {
        if ((data?.length ?? 0) === 0) {
            return undefined;
        }
        const locs = locationContext.selected.map(l => l.location);
        return data!.filter(d => locs.includes(d.location!) && ((movieId && d.movieMainVersionId === movieId) || !movieId));
    }, [locationContext.selected, movieId]);

    const { data, isFetching, isFetched } = useQuery({
        queryFn: getShowsAndMovies,
        queryKey: ["showsAndMovies"], // henter shows for HELE bygdekinoen
        staleTime: 1000 * 60 * 10,
        select: filterDataForSelectedLocations,
        enabled: locationContext.selected.length > 0
    });

    const { data: plannedDates, isFetching: isLoadingPlannedDates } = useQuery({
        queryFn: getPlannedDates,
        queryKey: ["plannedDates", ...(locationContext.selected.map(s => s._id))],
        staleTime: 3600,
        enabled: locationContext.selected.length > 0 && hidePlannedDates === false
    });

    return <div className={className}>
        {isFetching && <ShowPosterFallback numPlaceholderPosters={smallPlaceholders ? 2 : undefined} size={fallbackSize} />}
        {!isFetching && <>
            {(locationContext.selected?.length ?? 0) === 0 && <button className={styles.NoShowsBox} onClick={showLocationTools}><span><u>Velg sted</u> for å se ditt lokale kinoprogram.</span></button>}
            {isFetched && !data && <p className={styles.NoShowsBox}><span>Det er for øyeblikket ikke satt opp noen forestillinger.</span></p>}
            {data && <>
                {React.Children.map(children, child => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child, { shows: data, plannedDates, isLoadingPlannedDates, hidePlannedDates } as ProgramShowDisplayProps);
                    }
                    return child;
                })}
            </>}
        </>}
    </div>
}
//#endregion

//#region [Styles]

//#endregion